import { Component, ViewChild, computed, input, output } from '@angular/core';
import { Observable } from 'rxjs';
import { AppMatAutocompleteComponent } from '@xmedical/ui';
import { Medication } from '@xmedical/core';
import { WithReferralInfo, hasReferralInfo, formatReferralSource } from '../../types/referral-info';

/**
 * Generic interface for displayed medication items.
 */
export interface MedicationDisplayItem extends WithReferralInfo {
  id?: number;
  description: string;
  dosage?: string;
  diagnosisCode?: string;
  comment?: string;
  lastModifiedDate?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
  // For duplicate type detection
  typeId?: number;
  typeUnassigned?: boolean;
  // Optional text color
  textColour?: string;
}

/**
 * Event emitted when a medication is added from search results.
 */
export interface MedicationAddedEvent {
  item: Medication;
  dosage: string;
  diagnosisCode: string;
  comment: string;
}

/**
 * Event emitted when a medication is removed from the list.
 */
export interface MedicationRemovedEvent {
  item: MedicationDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-medication-widget-view',
  templateUrl: './medication-widget-view.component.html',
  styleUrls: ['./medication-widget-view.component.scss'],
  standalone: false
})
export class MedicationWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<MedicationDisplayItem[]>([]);
  title = input<string>('Medication');
  searchFn = input.required<(query: string) => Observable<Medication[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  addItemText = input<string>('Add new medication');

  // Outputs
  itemAdded = output<MedicationAddedEvent>();
  itemRemoved = output<MedicationRemovedEvent>();
  addNewItem = output<string>();

  // Track comment for search results before adding
  resultComments: { [key: number]: string } = {};

  // Computed signal for reactive hasItems check
  hasItems = computed(() => this.items().length > 0);

  getResultData(itemId: number): { comment: string } {
    if (!this.resultComments[itemId]) {
      this.resultComments[itemId] = '';
    }
    return { comment: this.resultComments[itemId] };
  }

  setResultComment(itemId: number, value: string): void {
    this.resultComments[itemId] = value;
  }

  onItemSelected(result: Medication): void {
    const comment = this.resultComments[result.id] || '';
    this.itemAdded.emit({
      item: result,
      dosage: result.dosage || '',
      diagnosisCode: result.diagnosisCode || '',
      comment
    });
    this.clearSearch();
  }

  onRemoveItem(item: MedicationDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  onAddNewItem(): void {
    const searchTerm = this.autocomplete?.queryText || '';
    this.clearSearch();  // Close search panel
    this.addNewItem.emit(searchTerm);
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

  // Referral helper methods for template
  isReferred(item: MedicationDisplayItem): boolean {
    return hasReferralInfo(item);
  }

  getReferralText(item: MedicationDisplayItem): string {
    return formatReferralSource(item);
  }

  // Duplicate detection methods
  hasDuplicates(item: MedicationDisplayItem): boolean {
    return this.items().some(m => {
      if (m.description.length > item.description.length) {
        return m.description.indexOf(item.description) > -1 && m.id !== item.id;
      } else if (m.description.length < item.description.length) {
        return item.description.indexOf(m.description) > -1 && m.id !== item.id;
      } else {
        return m.description === item.description && m.id !== item.id;
      }
    });
  }

  hasDuplicateTypes(item: MedicationDisplayItem): boolean {
    if (item.typeId === undefined || item.typeUnassigned) {
      return false;
    }
    return this.items().some(m => {
      return m.typeId !== undefined && item.typeId === m.typeId && item.id !== m.id;
    });
  }
}
